import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Calendar, Clock, MapPin, ExternalLink, ArrowLeft } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { BookingForm } from "@/components/BookingForm";
import { TicketPass } from "@/components/TicketPass";
import { ArtistCard } from "@/components/ArtistCard";
import { Gallery } from "@/components/Gallery";
import { Spinner } from "@/components/common";

const POSTER_FALLBACK = "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?crop=entropy&cs=srgb&fm=jpg&q=75&w=1200";

export default function EventDetails() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState(null);
  const bookingRef = useRef(null);

  useEffect(() => {
    api.get(`/events/${id}`).then((r) => setEvent(r.data)).catch(() => setEvent(null)).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!loading && event && params.get("book") === "1") {
      setTimeout(() => bookingRef.current?.scrollIntoView({ behavior: "smooth" }), 400);
    }
  }, [loading, event, params]);

  const scrollToBooking = () => bookingRef.current?.scrollIntoView({ behavior: "smooth" });

  if (loading) return <Spinner />;
  if (!event) return (
    <div className="mx-auto max-w-xl px-4 py-40 text-center">
      <h1 className="font-serif text-3xl">Event not found</h1>
      <Link to="/events" className="btn-outline mt-6">Back to events</Link>
    </div>
  );

  const poster = event.poster ? fileUrl(event.poster) : POSTER_FALLBACK;
  const isPast = event.status === "past";

  return (
    <div data-testid="event-details-page">
      {/* Hero */}
      <section className="relative pt-16">
        <div className="absolute inset-0 h-[60vh]">
          <img src={poster} alt="" className="h-full w-full object-cover opacity-25" onError={(e) => { e.currentTarget.src = POSTER_FALLBACK; }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] to-transparent" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-12 sm:px-8">
          <Link to="/events" className="mb-6 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#E5C158]"><ArrowLeft size={15} /> Back</Link>
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="overflow-hidden rounded-xl border border-[#2A2A32]">
              <img src={poster} alt={event.name} className="aspect-[4/3] w-full object-cover" onError={(e) => { e.currentTarget.src = POSTER_FALLBACK; }} />
            </div>
            <div>
              <span className="caption">{isPast ? "Past Event" : "Live Event"}</span>
              <h1 className="mt-2 font-serif text-4xl font-bold leading-tight sm:text-5xl">{event.name}</h1>
              <div className="mt-5 space-y-2 text-zinc-300">
                {event.date && <p className="flex items-center gap-3"><Calendar size={18} className="text-[#D4AF37]" />{event.date}</p>}
                {event.time && <p className="flex items-center gap-3"><Clock size={18} className="text-[#D4AF37]" />{event.time}</p>}
                {event.venue && <p className="flex items-center gap-3"><MapPin size={18} className="text-[#D4AF37]" />{event.venue}{event.address ? `, ${event.address}` : ""}</p>}
                {event.map_link && <a href={event.map_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-[#E5C158] hover:underline">View on map <ExternalLink size={13} /></a>}
              </div>
              {event.description && <p className="mt-6 leading-relaxed text-zinc-400">{event.description}</p>}
              {!isPast && (
                <button onClick={scrollToBooking} className="btn-gold mt-8 w-full sm:w-auto" data-testid="book-tickets-detail-button">BOOK TICKETS</button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Artists */}
      {event.artists?.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-8">
          <h2 className="mb-6 font-serif text-2xl font-bold sm:text-3xl">Lineup</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {event.artists.map((a) => <ArtistCard key={a.id} artist={a} />)}
          </div>
        </section>
      )}

      {/* Booking / Ticket */}
      {!isPast && (
        <section ref={bookingRef} className="mx-auto max-w-3xl scroll-mt-20 px-4 py-12 sm:px-8" id="booking" data-testid="booking-section">
          {ticket ? (
            <div data-testid="booking-success">
              <h2 className="mb-6 text-center font-serif text-3xl font-bold gold-text">Your Ticket is Ready!</h2>
              <TicketPass booking={ticket} />
              <div className="mt-6 text-center">
                <button onClick={() => setTicket(null)} className="btn-outline" data-testid="book-another-button">Book Another</button>
              </div>
            </div>
          ) : (
            <BookingForm event={event} onSuccess={setTicket} />
          )}
        </section>
      )}

      {/* Gallery */}
      {event.gallery?.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-8">
          <h2 className="mb-6 font-serif text-2xl font-bold sm:text-3xl">Gallery</h2>
          <Gallery images={event.gallery} />
        </section>
      )}
    </div>
  );
}
